import { useCallback, useEffect, useMemo, useState } from "react";
import { Archive, ChevronLeft, ChevronRight, Download, Loader2, LockKeyhole, Minus, MoreHorizontal, Pause, Plus, Printer, RotateCcw, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import AdminHeader from "@/components/latir/AdminHeader";
import { useSEO } from "@/hooks/useSEO";

const API = process.env.REACT_APP_BACKEND_URL;
export const TOKEN_KEY = "arteconciencia_admin_token_v1";

const DAYS = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
const MONTHS = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

function fmtDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  const dt = new Date(y, m - 1, d);
  return `${DAYS[dt.getDay()]} ${dt.getDate()} de ${MONTHS[dt.getMonth()]} de ${dt.getFullYear()}`;
}
function fmtTime(hhmm) {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map((n) => parseInt(n, 10));
  const ampm = h < 12 ? "a. m." : "p. m.";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}
function fmtDateTime(iso) {
  if (!iso) return "";
  const dt = new Date(iso);
  return `${DAYS[dt.getDay()]} ${dt.getDate()} de ${MONTHS[dt.getMonth()]} de ${dt.getFullYear()}, ${dt.getHours() % 12 || 12}:${String(dt.getMinutes()).padStart(2, "0")} ${dt.getHours() < 12 ? "a. m." : "p. m."}`;
}
function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/* ——— Estados: color + texto (nunca solo color) ——— */
const SLOT_STATUS = {
  active: { label: "Activa", cls: "text-emerald-700 border-emerald-700/60" },
  inactive: { label: "Inactiva", cls: "text-yellow-700 border-yellow-700/50" },
  cancelled: { label: "Cancelada", cls: "text-ink-mute border-line" },
  archived: { label: "Archivada", cls: "text-ink-mute border-line bg-line/20" },
};
const BOOKING_STATUS = {
  confirmed: { label: "Confirmada", cls: "text-latir-red" },
  pending: { label: "Pendiente", cls: "text-yellow-700" },
  cancelled: { label: "Cancelada", cls: "text-ink-mute" },
  archived: { label: "Archivada", cls: "text-ink-mute" },
};

/* Contadores de la visita: solo las reservas CONFIRMADAS ocupan cupo */
function capacityText(slot) {
  const avail = Math.max(0, slot.capacity - slot.confirmed_count);
  let base = `Capacidad total: ${slot.capacity} · Reservados: ${slot.confirmed_count} · Disponibles: ${avail} · Reservas confirmadas: ${slot.reservation_count ?? 0}`;
  if (slot.pending_count > 0) base += ` · Pendientes: ${slot.pending_count}`;
  return base;
}

function SlotBadges({ slot }) {
  const st = SLOT_STATUS[slot.status] || SLOT_STATUS.active;
  const confirmed = slot.confirmed_count > 0;
  const full = slot.confirmed_count >= slot.capacity;
  return (
    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1" data-testid={`admin-slot-state-${slot.id}`}>
      <span className={`inline-flex items-center gap-1.5 border px-2.5 py-1 font-ui uppercase tracking-[0.14em] text-[0.6rem] ${st.cls}`}>
        <span className="w-1.5 h-1.5 rounded-full bg-current inline-block" aria-hidden />
        {st.label}
      </span>
      {full ? (
        <span className="inline-flex items-center border border-latir-red bg-latir-red text-paper-raised px-2.5 py-1 font-ui uppercase tracking-[0.14em] text-[0.6rem]">
          Completa
        </span>
      ) : confirmed ? (
        <span className="inline-flex items-center border border-latir-red/60 text-latir-red px-2.5 py-1 font-ui uppercase tracking-[0.14em] text-[0.6rem]">
          Confirmada
        </span>
      ) : (
        <span className="inline-flex items-center border border-line text-ink-mute px-2.5 py-1 font-ui uppercase tracking-[0.14em] text-[0.6rem]">
          Sin reservas
        </span>
      )}
    </span>
  );
}

/* ——— Botones táctiles (mín. 44px) ——— */
const BTN = "inline-flex items-center justify-center gap-2 min-h-[44px] px-4 py-2 border font-ui uppercase tracking-[0.14em] text-[0.62rem] transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
const BTN_NEUTRAL = `${BTN} border-line text-ink-soft hover:border-ink hover:text-ink`;
const BTN_ACCENT = `${BTN} border-latir-red text-latir-red hover:bg-latir-red hover:text-paper-raised`;
const BTN_SOLID = `${BTN} border-ink bg-ink text-paper-raised hover:bg-latir-red hover:border-latir-red`;
const BTN_DANGER = `${BTN} border-latir-red bg-latir-red text-paper-raised`;

/** Small helper: authenticated fetch that returns parsed JSON or throws. */
export async function apiFetch(path, token, options = {}) {
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  if (res.status === 401) {
    localStorage.removeItem(TOKEN_KEY);
    window.location.reload();
    return;
  }
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const detail = data && data.detail;
    const msg = typeof detail === "string"
      ? detail
      : Array.isArray(detail)
        ? detail.map((x) => x.msg || JSON.stringify(x)).join("; ")
        : `Error ${res.status}`;
    throw new Error(msg);
  }
  return data;
}

/* ——— Confirmación inline en dos pasos (nada destructivo con un solo clic) ——— */
function ConfirmBlock({ text, confirmLabel, cancelLabel = "Volver", onConfirm, onCancel, busy, danger, testid }) {
  return (
    <div className={`mt-3 border-l-2 ${danger ? "border-latir-red" : "border-ink"} bg-paper-raised px-4 py-3`} data-testid={testid}>
      <p className={`font-ui text-[0.82rem] leading-relaxed ${danger ? "text-latir-red" : "text-ink-soft"}`}>{text}</p>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <button onClick={onConfirm} disabled={busy} className={danger ? BTN_DANGER : BTN_SOLID} data-testid={`${testid}-yes`}>
          {busy ? "Procesando…" : confirmLabel}
        </button>
        <button onClick={onCancel} disabled={busy} className={BTN_NEUTRAL} data-testid={`${testid}-no`}>
          {cancelLabel}
        </button>
      </div>
    </div>
  );
}

export default function AdminVisitas() {
  useSEO({ title: "Visitas guiadas | Admin Arteconciencia", noindex: true });
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  if (!token) return <AdminLogin onLogin={(t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); }} />;
  return <AdminDashboard token={token} onLogout={() => { localStorage.removeItem(TOKEN_KEY); setToken(null); }} />;
}

/* ————————————————————————————————————————————————— Login ——— */
export function AdminLogin({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/visits/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Contraseña incorrecta");
      onLogin(data.token);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="paper-grain min-h-screen bg-paper flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-sm text-center" data-testid="admin-login">
        <p className="eyebrow text-latir-red mb-6">Arteconciencia · Admin</p>
        <LockKeyhole className="w-6 h-6 text-ink-mute mx-auto mb-4" strokeWidth={1.4} />
        <h1 className="font-display text-[2rem] text-ink" style={{ fontFamily: '"Instrument Serif", serif' }}>
          Panel de visitas
        </h1>
        <p className="mt-3 font-editorial italic text-[1rem] text-ink-soft">Ingresa la contraseña administrativa.</p>
        <input
          type="password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Contraseña"
          className="mt-8 w-full bg-transparent border-b border-line focus:border-latir-red outline-none text-center font-ui text-[1rem] py-3 tracking-[0.1em]"
          data-testid="admin-password"
        />
        {error && (
          <p className="mt-3 font-ui uppercase text-[0.68rem] tracking-[0.2em] text-latir-red" data-testid="admin-login-error">{error}</p>
        )}
        <button
          type="submit"
          disabled={busy || !password}
          className="mt-8 inline-flex items-center gap-3 bg-ink text-paper-raised px-6 py-3 font-ui uppercase tracking-[0.2em] text-[0.72rem] hover:bg-latir-red transition-colors disabled:opacity-50"
          data-testid="admin-login-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Entrar"}
        </button>
      </form>
    </div>
  );
}

/* ————————————————————————————————————————————— Dashboard ——— */
const VISIT_FILTERS = [
  ["all", "Todas"],
  ["active", "Activas"],
  ["cancelled", "Canceladas"],
  ["past", "Pasadas"],
  ["archived", "Archivadas"],
];

function AdminDashboard({ token, onLogout }) {
  const [slots, setSlots] = useState([]);
  const [pending, setPending] = useState([]);
  const [selectedSlotId, setSelectedSlotId] = useState(null);
  const [detail, setDetail] = useState(null); // {slot, bookings}
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editSlot, setEditSlot] = useState(null);
  const [addFor, setAddFor] = useState(null); // slot para crear reserva manual
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState("lista"); // "lista" | "calendario"
  const [error, setError] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [sl, pnd] = await Promise.all([
        apiFetch("/api/visits/admin/slots", token),
        apiFetch("/api/visits/admin/bookings/pending", token),
      ]);
      setSlots(sl || []);
      setPending(pnd || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadDetail = useCallback(async (slotId) => {
    setSelectedSlotId(slotId);
    setDetail(null);
    try {
      const d = await apiFetch(`/api/visits/admin/slots/${slotId}/bookings`, token);
      setDetail(d);
    } catch (e) {
      setError(e.message);
    }
  }, [token]);

  const refresh = useCallback(() => {
    loadAll();
    if (selectedSlotId) loadDetail(selectedSlotId);
  }, [loadAll, loadDetail, selectedSlotId]);

  /* Abrir detalle desde la lista o el calendario: además de cargar, baja la
     página hasta el panel de detalle (con 80+ visitas, si no, parece que
     "Ver reservas" no hace nada). */
  const openDetail = useCallback((id) => {
    loadDetail(id);
    setTimeout(() => document.getElementById("slot-detail-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
  }, [loadDetail]);

  const exportCsv = async () => {
    const res = await fetch(`${API}/api/visits/admin/export.csv`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "reservas-latir.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const visibleSlots = useMemo(() => {
    const today = todayStr();
    switch (filter) {
      case "active": return slots.filter((s) => s.status === "active");
      case "cancelled": return slots.filter((s) => s.status === "cancelled");
      case "past": return slots.filter((s) => s.status !== "archived" && s.date < today);
      case "archived": return slots.filter((s) => s.status === "archived");
      default: return slots.filter((s) => s.status !== "archived");
    }
  }, [slots, filter]);

  return (
    <div className="paper-grain min-h-screen bg-paper pb-24">
      <AdminHeader onLogout={onLogout} />
      <div className="max-w-6xl mx-auto px-6 sm:px-10 pt-10">
        <div className="flex items-baseline justify-between gap-6 flex-wrap">
          <div>
            <p className="eyebrow text-latir-red">Arteconciencia · Admin</p>
            <h1 className="mt-2 font-display text-[2rem] sm:text-[2.6rem] text-ink" style={{ fontFamily: '"Instrument Serif", serif' }}>
              Visitas guiadas
            </h1>
          </div>
          <button
            onClick={exportCsv}
            className="inline-flex items-center gap-2 px-4 min-h-[44px] border border-line hover:border-ink font-ui uppercase tracking-[0.18em] text-[0.68rem] text-ink-soft hover:text-ink transition-colors"
            data-testid="admin-export-csv"
          >
            <Download className="w-3.5 h-3.5" /> Descargar CSV
          </button>
        </div>

        {error && (
          <div className="mt-6 border-l-2 border-latir-red px-4 py-3 font-ui text-[0.82rem] text-ink-soft" data-testid="admin-error">
            {error}
          </div>
        )}

        {/* Solicitudes pendientes de confirmación */}
        {pending.length > 0 && (
          <section className="mt-10">
            <h2 className="font-ui uppercase tracking-[0.24em] text-[0.75rem] text-latir-red">
              Solicitudes pendientes de confirmación ({pending.length})
            </h2>
            <ul className="mt-4 border-t border-b border-line divide-y divide-line" data-testid="admin-pending">
              {pending.map((b) => (
                <PendingRow key={b.id} b={b} slots={slots} token={token} onChange={refresh} />
              ))}
            </ul>
          </section>
        )}

        {/* Visitas */}
        <section className="mt-12">
          <div className="flex items-baseline justify-between gap-4 flex-wrap">
            <h2 className="font-ui uppercase tracking-[0.24em] text-[0.75rem] text-ink">Visitas programadas</h2>
            <button
              onClick={() => setShowNew(true)}
              className="inline-flex items-center gap-2 min-h-[44px] font-ui uppercase tracking-[0.18em] text-[0.68rem] text-latir-red hover:text-ink transition-colors"
              data-testid="admin-new-slot"
            >
              <Plus className="w-3.5 h-3.5" /> Nueva visita
            </button>
          </div>

          <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="admin-visit-filters">
            {VISIT_FILTERS.map(([key, label]) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`px-3 min-h-[44px] border font-ui uppercase tracking-[0.14em] text-[0.62rem] transition-colors ${filter === key && view === "lista" ? "border-ink text-ink bg-paper-raised" : "border-line text-ink-mute hover:text-ink hover:border-ink"}`}
                data-testid={`admin-vfilter-${key}`}
              >
                {label}
              </button>
            ))}
            <span className="mx-1 h-6 w-px bg-line" aria-hidden />
            {[["lista", "Lista"], ["calendario", "Calendario"]].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setView(key)}
                className={`px-3 min-h-[44px] border font-ui uppercase tracking-[0.14em] text-[0.62rem] transition-colors ${view === key ? "border-ink bg-ink text-paper-raised" : "border-line text-ink-mute hover:text-ink hover:border-ink"}`}
                data-testid={`admin-view-${key}`}
              >
                {label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="mt-6 flex items-center gap-3 text-ink-mute font-ui text-[0.9rem]">
              <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
            </div>
          ) : view === "calendario" ? (
            <VisitasCalendar
              slots={slots.filter((s) => s.status !== "archived")}
              onOpenSlot={openDetail}
            />
          ) : (
            <ul className="mt-4 border-t border-b border-line divide-y divide-line" data-testid="admin-slots">
              {visibleSlots.map((s) => (
                <SlotCard
                  key={s.id}
                  slot={s}
                  selected={selectedSlotId === s.id}
                  onSelect={() => openDetail(s.id)}
                  token={token}
                  onChange={refresh}
                  onEditSlot={() => setEditSlot(s)}
                  onAddBooking={() => setAddFor(s)}
                />
              ))}
              {visibleSlots.length === 0 && (
                <li className="py-8 font-editorial italic text-ink-soft">
                  No hay visitas en esta vista.
                </li>
              )}
            </ul>
          )}
        </section>

        {/* Detalle de la visita */}
        {detail && (
          <div id="slot-detail-anchor" className="scroll-mt-24">
            <SlotDetail
              detail={detail}
              slots={slots}
              token={token}
              onChange={refresh}
              onClose={() => { setSelectedSlotId(null); setDetail(null); }}
              onAddBooking={() => setAddFor(detail.slot)}
            />
          </div>
        )}
      </div>

      {showNew && (
        <NewSlotModal
          token={token}
          onClose={() => setShowNew(false)}
          onSaved={() => { setShowNew(false); refresh(); toast.success("Visita creada"); }}
        />
      )}
      {editSlot && (
        <EditSlotModal
          slot={editSlot}
          token={token}
          onClose={() => setEditSlot(null)}
          onSaved={() => { setEditSlot(null); refresh(); toast.success("Visita actualizada"); }}
        />
      )}
      {addFor && (
        <AddBookingModal
          slot={addFor}
          slots={slots}
          token={token}
          onClose={() => setAddFor(null)}
          onSaved={() => { setAddFor(null); refresh(); toast.success("Reserva creada"); }}
        />
      )}
    </div>
  );
}

/* ——————————————————————————————————— Vista mensual (calendario) ——— */
const WEEKDAYS_SHORT = ["L", "M", "X", "J", "V", "S", "D"];

function VisitasCalendar({ slots, onOpenSlot }) {
  const today = todayStr();
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const byDate = useMemo(() => {
    const m = {};
    for (const s of slots) (m[s.date] = m[s.date] || []).push(s);
    Object.values(m).forEach((arr) => arr.sort((a, b) => a.time.localeCompare(b.time)));
    return m;
  }, [slots]);

  const y = cursor.getFullYear();
  const mo = cursor.getMonth();
  const firstOffset = (new Date(y, mo, 1).getDay() + 6) % 7; // lunes = 0
  const daysInMonth = new Date(y, mo + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(`${y}-${String(mo + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
  }

  return (
    <div className="mt-4" data-testid="admin-calendar">
      <div className="flex items-center justify-between gap-4">
        <button onClick={() => setCursor(new Date(y, mo - 1, 1))} className={`${BTN_NEUTRAL} !px-3`} aria-label="Mes anterior" data-testid="admin-cal-prev">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <p className="font-display text-[1.3rem] text-ink capitalize" style={{ fontFamily: '"Instrument Serif", serif' }} data-testid="admin-cal-title">
          {MONTHS[mo]} de {y}
        </p>
        <button onClick={() => setCursor(new Date(y, mo + 1, 1))} className={`${BTN_NEUTRAL} !px-3`} aria-label="Mes siguiente" data-testid="admin-cal-next">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
      <div className="mt-4 grid grid-cols-7 gap-px bg-line border border-line" data-testid="admin-cal-grid">
        {WEEKDAYS_SHORT.map((w, i) => (
          <div key={i} className="bg-paper-raised px-1 py-2 text-center font-ui uppercase tracking-[0.18em] text-[0.58rem] text-ink-mute">{w}</div>
        ))}
        {cells.map((dateStr, i) => {
          if (!dateStr) return <div key={`empty-${i}`} className="bg-paper min-h-[74px] sm:min-h-[96px]" />;
          const day = parseInt(dateStr.slice(-2), 10);
          const daySlots = byDate[dateStr] || [];
          const isToday = dateStr === today;
          const isPast = dateStr < today;
          return (
            <div key={dateStr} className={`bg-paper min-h-[74px] sm:min-h-[96px] p-1 sm:p-1.5 ${isPast ? "opacity-55" : ""}`} data-testid={`admin-cal-day-${dateStr}`}>
              <p className={`text-right font-ui text-[0.62rem] ${isToday ? "text-latir-red" : "text-ink-mute"}`}>
                {isToday ? (
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-latir-red">{day}</span>
                ) : day}
              </p>
              <div className="mt-0.5 space-y-0.5">
                {daySlots.map((s) => {
                  const full = s.confirmed_count >= s.capacity;
                  const has = s.confirmed_count > 0;
                  const base = "block w-full text-left px-1 py-0.5 font-ui text-[0.55rem] sm:text-[0.6rem] leading-tight truncate border transition-colors";
                  const cls = s.status === "cancelled"
                    ? `${base} border-line text-ink-mute line-through`
                    : s.status === "inactive"
                      ? `${base} border-yellow-700/50 text-yellow-700`
                      : full
                        ? `${base} border-latir-red bg-latir-red text-paper-raised`
                        : has
                          ? `${base} border-latir-red/60 text-latir-red hover:bg-latir-red hover:text-paper-raised`
                          : `${base} border-line text-ink-soft hover:border-ink`;
                  return (
                    <button
                      key={s.id}
                      onClick={() => onOpenSlot(s.id)}
                      className={cls}
                      title={`${fmtDate(s.date)} · ${fmtTime(s.time)} — ${s.confirmed_count}/${s.capacity}`}
                      data-testid={`admin-cal-chip-${s.id}`}
                    >
                      {s.time}
                      <span className="hidden sm:inline"> · {s.confirmed_count}/{s.capacity}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-3 font-ui text-[0.62rem] uppercase tracking-[0.16em] text-ink-mute">
        Toca un horario para ver sus reservas · gris: sin reservas · rojo: confirmada · rojo sólido: completa · tachado: cancelada
      </p>
    </div>
  );
}

/* ————————————————————————————————————————————— Tarjeta de visita ——— */
function SlotCard({ slot, selected, onSelect, token, onChange, onEditSlot, onAddBooking }) {
  const [confirming, setConfirming] = useState(null); // null | "cancel" | "archive"
  const [menuOpen, setMenuOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const act = async (path, okMsg) => {
    setBusy(true);
    try {
      await apiFetch(path, token, { method: "POST" });
      toast.success(okMsg);
      setConfirming(null);
      setMenuOpen(false);
      onChange();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const closeTemporal = async () => {
    setBusy(true);
    try {
      await apiFetch(`/api/visits/admin/slots/${slot.id}`, token, {
        method: "PATCH",
        body: JSON.stringify({ status: "inactive" }),
      });
      toast.success("Visita cerrada temporalmente.");
      setMenuOpen(false);
      onChange();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className={selected ? "bg-paper-raised" : ""} data-testid={`admin-slot-${slot.id}`}>
      <div className="py-5">
        <button onClick={onSelect} className="w-full text-left" data-testid={`admin-slot-open-${slot.id}`}>
          <p className="font-display text-[1.15rem] text-ink capitalize" style={{ fontFamily: '"Instrument Serif", serif' }}>
            {fmtDate(slot.date)} · {fmtTime(slot.time)}
          </p>
          <div className="mt-1.5">
            <SlotBadges slot={slot} />
          </div>
          <p className="mt-1.5 font-ui text-[0.72rem] text-ink-soft" data-testid={`admin-slot-summary-${slot.id}`}>
            {capacityText(slot)}
          </p>
          {slot.notes && (
            <p className="mt-1 font-editorial italic text-[0.85rem] text-ink-mute">{slot.notes}</p>
          )}
        </button>

        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <button onClick={onSelect} className={BTN_SOLID} data-testid={`admin-slot-view-${slot.id}`}>
            Ver reservas
          </button>
          {slot.status === "active" && (
            <>
              <button onClick={onAddBooking} className={BTN_ACCENT} data-testid={`admin-slot-add-${slot.id}`}>
                <Plus className="w-3.5 h-3.5" /> Agregar reserva
              </button>
              <button onClick={onEditSlot} className={BTN_NEUTRAL} data-testid={`admin-slot-edit-${slot.id}`}>
                Editar visita
              </button>
              <button onClick={() => { setConfirming("cancel"); setMenuOpen(false); }} className={BTN_NEUTRAL} data-testid={`admin-slot-cancel-${slot.id}`}>
                Cancelar visita
              </button>
            </>
          )}
          {(slot.status === "cancelled" || slot.status === "inactive") && (
            <>
              <button onClick={onEditSlot} className={BTN_NEUTRAL} data-testid={`admin-slot-edit-${slot.id}`}>
                Editar visita
              </button>
              <button
                onClick={() => act(`/api/visits/admin/slots/${slot.id}/reactivate`, "Visita reactivada correctamente.")}
                disabled={busy}
                className={BTN_ACCENT}
                data-testid={`admin-slot-reactivate-${slot.id}`}
              >
                <RotateCcw className="w-3.5 h-3.5" /> Reactivar visita
              </button>
            </>
          )}
          {slot.status === "archived" && (
            <button
              onClick={() => setConfirming("restore")}
              disabled={busy}
              className={BTN_ACCENT}
              data-testid={`admin-slot-restore-${slot.id}`}
            >
              <RotateCcw className="w-3.5 h-3.5" /> Restaurar visita
            </button>
          )}

          {/* ⋯ Más opciones */}
          <div className="relative">
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className={`${BTN_NEUTRAL} !px-3`}
              aria-label="Más opciones"
              data-testid={`admin-slot-more-${slot.id}`}
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 z-20 mt-1 w-56 border border-line bg-paper-raised shadow-lg" data-testid={`admin-slot-menu-${slot.id}`}>
                <a
                  href={`/admin/visitas/imprimir?date=${slot.date}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 px-4 py-3 font-ui uppercase tracking-[0.14em] text-[0.62rem] text-ink-soft hover:bg-paper hover:text-ink"
                  data-testid={`admin-slot-print-${slot.id}`}
                >
                  <Printer className="w-3.5 h-3.5" /> Imprimir hoja del día
                </a>
                {slot.status === "active" && (
                  <button
                    onClick={closeTemporal}
                    className="w-full flex items-center gap-2 px-4 py-3 font-ui uppercase tracking-[0.14em] text-[0.62rem] text-ink-soft hover:bg-paper hover:text-ink text-left"
                    data-testid={`admin-slot-inactivate-${slot.id}`}
                  >
                    <Pause className="w-3.5 h-3.5" /> Cerrar temporalmente
                  </button>
                )}
                {slot.status !== "archived" && (
                  <button
                    onClick={() => { setConfirming("archive"); setMenuOpen(false); }}
                    className="w-full flex items-center gap-2 px-4 py-3 font-ui uppercase tracking-[0.14em] text-[0.62rem] text-ink-soft hover:bg-paper hover:text-ink text-left"
                    data-testid={`admin-slot-archive-${slot.id}`}
                  >
                    <Archive className="w-3.5 h-3.5" /> Archivar visita
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {confirming === "cancel" && (
          <ConfirmBlock
            text="¿Cancelar esta visita guiada? Esta visita dejará de estar disponible para nuevas reservas, pero permanecerá guardada y podrá reactivarse posteriormente."
            confirmLabel="Sí, cancelar visita"
            cancelLabel="Regresar"
            busy={busy}
            testid={`admin-slot-cancel-confirm-${slot.id}`}
            onCancel={() => setConfirming(null)}
            onConfirm={() => act(`/api/visits/admin/slots/${slot.id}/cancel`, "Visita cancelada correctamente.")}
          />
        )}
        {confirming === "archive" && (
          <ConfirmBlock
            text="¿Archivar esta visita? Podrás conservar su historial, pero dejará de aparecer en la operación normal."
            confirmLabel="Archivar visita"
            busy={busy}
            testid={`admin-slot-archive-confirm-${slot.id}`}
            onCancel={() => setConfirming(null)}
            onConfirm={() => act(`/api/visits/admin/slots/${slot.id}/archive`, "Visita archivada.")}
          />
        )}
        {confirming === "restore" && (
          <ConfirmBlock
            text="¿Restaurar esta visita? Volverá a estar activa y visible al público, conservando su fecha, hora, capacidad y todas sus reservas."
            confirmLabel="Restaurar visita"
            busy={busy}
            testid={`admin-slot-restore-confirm-${slot.id}`}
            onCancel={() => setConfirming(null)}
            onConfirm={() => act(`/api/visits/admin/slots/${slot.id}/reactivate`, "Visita restaurada correctamente.")}
          />
        )}
      </div>
    </li>
  );
}

/* ———————————————————————————————————— Modal base (mobile-first) ——— */
function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-ink/40 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div
        className="w-full sm:max-w-lg bg-paper border border-line px-6 sm:px-8 pt-6 overflow-y-auto max-h-[90dvh]"
        style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}
        onClick={(e) => e.stopPropagation()}
        data-testid={testid}
      >
        <div className="flex items-baseline justify-between gap-4">
          <h3 className="font-display text-[1.4rem] text-ink" style={{ fontFamily: '"Instrument Serif", serif' }}>
            {title}
          </h3>
          <button onClick={onClose} className="p-2 text-ink-mute hover:text-ink" aria-label="Cerrar" data-testid={`${testid}-close`}>
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mt-5">{children}</div>
      </div>
    </div>
  );
}

const FIELD = "mt-1 w-full bg-transparent border-b border-line focus:border-latir-red outline-none py-2.5 font-ui text-[0.95rem] text-ink";
const LABEL = "eyebrow text-ink-mute";

/* ——————————————————————————————————— Nueva visita ——— */
function NewSlotModal({ token, onClose, onSaved }) {
  const [form, setForm] = useState({ date: "", time: "", capacity: 40, notes: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await apiFetch("/api/visits/admin/slots", token, {
        method: "POST",
        body: JSON.stringify({ date: form.date, time: form.time, capacity: Number(form.capacity), notes: form.notes.trim() }),
      });
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title="Nueva visita guiada" onClose={onClose} testid="admin-new-slot-modal">
      <div className="space-y-4">
        <label className="block">
          <span className={LABEL}>Fecha</span>
          <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={FIELD} data-testid="new-slot-date" />
        </label>
        <label className="block">
          <span className={LABEL}>Hora</span>
          <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className={FIELD} data-testid="new-slot-time" />
        </label>
        <label className="block">
          <span className={LABEL}>Capacidad recomendada</span>
          <input type="number" min="1" max="500" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className={FIELD} data-testid="new-slot-capacity" />
        </label>
        <label className="block">
          <span className={LABEL}>Notas (opcional)</span>
          <input type="text" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={FIELD} data-testid="new-slot-notes" />
        </label>
        {error && <p className="font-ui text-[0.78rem] text-latir-red" data-testid="new-slot-error">{error}</p>}
        <div className="flex items-center gap-2 pt-2">
          <button onClick={save} disabled={busy || !form.date || !form.time} className={BTN_SOLID} data-testid="new-slot-save">
            {busy ? "Guardando…" : "Crear visita"}
          </button>
          <button onClick={onClose} disabled={busy} className={BTN_NEUTRAL}>Cancelar</button>
        </div>
      </div>
    </Modal>
  );
}

/* ——————————————————————————————————— Editar visita ——— */
function EditSlotModal({ slot, token, onClose, onSaved }) {
  const [form, setForm] = useState({ date: slot.date, time: slot.time, capacity: slot.capacity, notes: slot.notes || "" });
  const [confirmSchedule, setConfirmSchedule] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // El admin puede escribir CUALQUIER hora (input libre, sin lista pública)
  const scheduleChanged = form.date !== slot.date || form.time !== slot.time;

  const doSave = async () => {
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/api/visits/admin/slots/${slot.id}`, token, {
        method: "PATCH",
        body: JSON.stringify({ date: form.date, time: form.time, capacity: Number(form.capacity), notes: form.notes.trim() }),
      });
      onSaved();
    } catch (e) {
      setError(e.message);
      setConfirmSchedule(false);
    } finally {
      setBusy(false);
    }
  };
  const save = () => {
    // Cambiar fecha/hora con reservas existentes pide confirmación explícita
    if (scheduleChanged && slot.confirmed_count > 0 && !confirmSchedule) {
      setConfirmSchedule(true);
      return;
    }
    doSave();
  };

  return (
    <Modal title="Editar visita" onClose={onClose} testid="admin-edit-slot-modal">
      <div className="space-y-4">
        <label className="block">
          <span className={LABEL}>Fecha</span>
          <input type="date" value={form.date} onChange={(e) => { setForm({ ...form, date: e.target.value }); setConfirmSchedule(false); }} className={FIELD} data-testid="edit-slot-date" />
        </label>
        <label className="block">
          <span className={LABEL}>Hora (libre, sin restricción de horarios públicos)</span>
          <input type="time" value={form.time} onChange={(e) => { setForm({ ...form, time: e.target.value }); setConfirmSchedule(false); }} className={FIELD} data-testid="edit-slot-time" />
        </label>
        <label className="block">
          <span className={LABEL}>Capacidad total</span>
          <input type="number" min="1" max="500" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className={FIELD} data-testid="edit-slot-capacity" />
        </label>
        <label className="block">
          <span className={LABEL}>Notas</span>
          <input type="text" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={FIELD} data-testid="edit-slot-notes" />
        </label>
        {confirmSchedule && (
          <div className="border-l-2 border-ink bg-paper-raised px-4 py-3" data-testid="edit-slot-schedule-confirm">
            <p className="font-ui text-[0.82rem] leading-relaxed text-ink-soft">
              Esta visita tiene {slot.confirmed_count} participantes reservados. ¿Deseas cambiar el horario de {fmtDate(slot.date)} · {fmtTime(slot.time)} a {fmtDate(form.date)} · {fmtTime(form.time)}?
            </p>
          </div>
        )}
        {error && <p className="font-ui text-[0.78rem] text-latir-red" data-testid="edit-slot-error">{error}</p>}
        <div className="flex items-center gap-2 pt-2 flex-wrap">
          <button onClick={save} disabled={busy} className={BTN_SOLID} data-testid="edit-slot-save">
            {busy ? "Guardando…" : confirmSchedule ? "Confirmar cambio" : "Guardar"}
          </button>
          <button onClick={onClose} disabled={busy} className={BTN_NEUTRAL} data-testid="edit-slot-cancel">
            Cancelar
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* ——————————————————————————— Stepper de participantes (± y libre) ——— */
function PartyStepper({ value, onChange, testid }) {
  const clamp = (v) => Math.max(1, Math.min(500, v));
  return (
    <div className="mt-1 flex items-center gap-3">
      <button type="button" onClick={() => onChange(clamp(value - 1))} className="w-11 h-11 border border-line flex items-center justify-center hover:border-latir-red hover:text-latir-red transition-colors" aria-label="Menos" data-testid={`${testid}-minus`}>
        <Minus className="w-4 h-4" />
      </button>
      <input
        type="number"
        min="1"
        max="500"
        step="1"
        value={value}
        onChange={(e) => {
          const v = parseInt(e.target.value, 10);
          if (!Number.isNaN(v)) onChange(clamp(v));
        }}
        className="w-24 text-center bg-transparent border-b border-line focus:border-latir-red outline-none font-display text-[2rem] text-ink py-1"
        style={{ fontFamily: '"Instrument Serif", serif' }}
        data-testid={testid}
        aria-label="Número de personas"
      />
      <button type="button" onClick={() => onChange(clamp(value + 1))} className="w-11 h-11 border border-line flex items-center justify-center hover:border-latir-red hover:text-latir-red transition-colors" aria-label="Más" data-testid={`${testid}-plus`}>
        <Plus className="w-4 h-4" />
      </button>
    </div>
  );
}

/* ——————————————————————————— Crear reserva manual (admin) ——— */
function AddBookingModal({ slot, slots, token, onClose, onSaved }) {
  const active = slots.filter((s) => s.status === "active");
  const [form, setForm] = useState({
    name: "", email: "", phone: "", party_size: 1,
    slot_id: slot.id, notes: "", status: "confirmed",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const target = slots.find((s) => s.id === form.slot_id) || slot;

  const doSave = async () => {
    setBusy(true);
    setError(null);
    try {
      await apiFetch("/api/visits/admin/bookings", token, {
        method: "POST",
        body: JSON.stringify({
          slot_id: form.slot_id,
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          party_size: form.party_size,
          notes: form.notes.trim(),
          status: form.status,
        }),
      });
      onSaved();
    } catch (e) {
      setError(e.message); // capacidad dura: el backend responde con los espacios disponibles
    } finally {
      setBusy(false);
    }
  };
  const valid = form.name.trim().length >= 2; // email y teléfono opcionales para el admin

  return (
    <Modal title="Agregar reserva" onClose={onClose} testid="admin-add-booking-modal">
      <p className="font-ui text-[0.75rem] text-ink-soft mb-4" data-testid="add-booking-capacity-info">
        Capacidad total: {target.capacity} · Reservados: {target.confirmed_count} · Disponibles: {Math.max(0, target.capacity - target.confirmed_count)} · Pendientes: {target.pending_count}
      </p>
      <div className="space-y-4">
        <label className="block">
          <span className={LABEL}>Visita</span>
          <select value={form.slot_id} onChange={(e) => setForm({ ...form, slot_id: e.target.value })} className={FIELD} data-testid="add-booking-slot">
            {active.map((s) => (
              <option key={s.id} value={s.id}>
                {`${fmtDate(s.date)} · ${fmtTime(s.time)} — ${s.confirmed_count}/${s.capacity}`}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className={LABEL}>Nombre</span>
          <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={FIELD} data-testid="add-booking-name" />
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block">
            <span className={LABEL}>Email (opcional)</span>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={FIELD} data-testid="add-booking-email" />
          </label>
          <label className="block">
            <span className={LABEL}>Teléfono (opcional)</span>
            <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={FIELD} data-testid="add-booking-phone" />
          </label>
        </div>
        <div>
          <span className={LABEL}>Número de personas</span>
          <PartyStepper value={form.party_size} onChange={(v) => setForm({ ...form, party_size: v })} testid="add-booking-party" />
        </div>
        <label className="block">
          <span className={LABEL}>Notas (opcional)</span>
          <input type="text" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={FIELD} data-testid="add-booking-notes" />
        </label>
        <div>
          <span className={LABEL}>Estado</span>
          <div className="mt-2 flex gap-2">
            {[["confirmed", "Confirmada"], ["pending", "Pendiente"]].map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => setForm({ ...form, status: k })}
                className={`${form.status === k ? BTN_SOLID : BTN_NEUTRAL} flex-1`}
                data-testid={`add-booking-status-${k}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        {error && <p className="font-ui text-[0.78rem] text-latir-red" data-testid="add-booking-error">{error}</p>}
        <div className="flex items-center gap-2 pt-2">
          <button onClick={doSave} disabled={busy || !valid} className={BTN_SOLID} data-testid="add-booking-save">
            {busy ? "Guardando…" : "Guardar reserva"}
          </button>
          <button onClick={onClose} disabled={busy} className={BTN_NEUTRAL}>Cancelar</button>
        </div>
      </div>
    </Modal>
  );
}

/* ——————————————————————————————————— Detalle de la visita ——— */
const BOOKING_FILTERS = [
  ["all", "Todas"],
  ["confirmed", "Confirmadas"],
  ["pending", "Pendientes"],
  ["cancelled", "Canceladas"],
  ["archived", "Archivadas"],
];

function SlotDetail({ detail, slots, token, onChange, onClose, onAddBooking }) {
  const { slot: rawSlot, bookings } = detail;
  // Número de reservas confirmadas calculado desde la fuente de verdad (DB)
  const slot = { ...rawSlot, reservation_count: bookings.filter((x) => x.status === "confirmed").length };
  const [filter, setFilter] = useState("all");
  const visible = filter === "all"
    ? bookings.filter((x) => x.status !== "archived")
    : bookings.filter((x) => x.status === filter);
  return (
    <section className="mt-10 border-t border-line pt-10" data-testid="admin-slot-detail">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow text-latir-red">Detalle de la visita</p>
          <h3 className="mt-2 font-display text-[1.6rem] text-ink capitalize" style={{ fontFamily: '"Instrument Serif", serif' }}>
            {fmtDate(slot.date)} · {fmtTime(slot.time)}
          </h3>
          <p className="mt-1 font-ui text-[0.72rem] text-ink-soft" data-testid="admin-slot-summary">
            {capacityText(slot)}
          </p>
          <div className="mt-2">
            <SlotBadges slot={slot} />
          </div>
          {Array.isArray(slot.history) && slot.history.length > 0 && (
            <div className="mt-3" data-testid="admin-slot-history">
              <p className="font-ui uppercase tracking-[0.18em] text-[0.6rem] text-ink-mute">Historial</p>
              <ul className="mt-1 space-y-0.5">
                {slot.history.slice(-5).reverse().map((h, i) => (
                  <li key={i} className="font-editorial italic text-[0.78rem] text-ink-mute">
                    {fmtDateTime(h.at)} — {h.detail}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onAddBooking} className={BTN_ACCENT} data-testid="admin-detail-add-booking">
            <Plus className="w-3.5 h-3.5" /> Agregar reserva
          </button>
          <button onClick={onClose} className="p-2 text-ink-mute hover:text-ink" aria-label="Cerrar">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {bookings.length > 0 && (
        <div className="mt-5 flex items-center gap-2 flex-wrap" data-testid="admin-booking-filters">
          {BOOKING_FILTERS.map(([key, label]) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-3 min-h-[44px] border font-ui uppercase tracking-[0.14em] text-[0.62rem] transition-colors ${filter === key ? "border-ink text-ink bg-paper-raised" : "border-line text-ink-mute hover:text-ink hover:border-ink"}`}
              data-testid={`admin-filter-${key}`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      <ul className="mt-6 border-t border-b border-line divide-y divide-line">
        {visible.length === 0 && (
          <li className="py-6 font-editorial italic text-ink-soft">
            {bookings.length === 0 ? "Aún no hay reservas en esta visita." : "No hay reservas con este estado."}
          </li>
        )}
        {visible.map((b, i) => (
          <BookingCard key={b.id} b={b} n={i + 1} slot={slot} slots={slots} token={token} onChange={onChange} />
        ))}
      </ul>
    </section>
  );
}

/* ——————————————————————————————————— Tarjeta de reserva ——— */
function BookingCard({ b, n, slot, slots, token, onChange }) {
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(null); // null | "cancel" | "archive" | "delete"
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const st = BOOKING_STATUS[b.status] || { label: b.status, cls: "text-ink-soft" };

  const patch = async (payload, okMsg) => {
    setBusy(true);
    try {
      await apiFetch(`/api/visits/admin/bookings/${b.id}`, token, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      toast.success(okMsg);
      setConfirming(null);
      setReactivateOpen(false);
      setEditing(false);
      onChange();
    } catch (e) {
      toast.error(e.message || "No se pudo guardar el cambio. Intenta nuevamente.");
    } finally {
      setBusy(false);
    }
  };

  const post = async (path, okMsg) => {
    setBusy(true);
    try {
      await apiFetch(path, token, { method: "POST" });
      toast.success(okMsg);
      setConfirming(null);
      onChange();
    } catch (e) {
      toast.error(e.message || "No se pudo guardar el cambio. Intenta nuevamente.");
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    setBusy(true);
    try {
      await apiFetch(`/api/visits/admin/bookings/${b.id}`, token, { method: "DELETE" });
      toast.success("Reserva eliminada permanentemente");
      setConfirming(null);
      onChange();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="py-4" data-testid={`admin-booking-${b.id}`}>
      <div className="flex items-start gap-4 flex-wrap">
        <span className="font-ui text-[0.72rem] text-ink-mute w-6 pt-1">{String(n).padStart(2, "0")}</span>
        <div className="flex-1 min-w-[180px]">
          <p className="font-editorial text-[1.05rem] text-ink">{b.name}</p>
          <p className="mt-1 font-ui text-[0.78rem] text-ink-soft">{b.email} · {b.phone}</p>
          <p className="mt-1 font-ui text-[0.64rem] leading-[1.5] text-ink-mute">
            Visita: {fmtDate(b.date)} · {fmtTime(b.time)} · Reservada el {fmtDateTime(b.created_at)}
            <br />
            ID {b.id}
            {b.notes ? ` · Comentarios: ${b.notes}` : ""}
          </p>
        </div>
        <span className="font-ui text-[0.85rem] text-ink text-right">{b.party_size} {b.party_size === 1 ? "persona" : "personas"}</span>
        <span className={`font-ui uppercase tracking-[0.16em] text-[0.66rem] text-right ${st.cls}`} data-testid={`admin-booking-status-${b.id}`}>
          {st.label}
        </span>
      </div>

      {/* Botones según estado */}
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <button onClick={() => setEditing(true)} className={BTN_NEUTRAL} data-testid={`admin-booking-edit-${b.id}`}>
          Editar
        </button>
        {b.status === "pending" && (
          <button
            onClick={() => patch({ status: "confirmed" }, "Reserva confirmada")}
            disabled={busy}
            className={BTN_ACCENT}
            data-testid={`admin-booking-confirm-${b.id}`}
          >
            Confirmar reserva
          </button>
        )}
        {(b.status === "confirmed" || b.status === "pending") && (
          <button onClick={() => setConfirming("cancel")} disabled={busy} className={BTN_NEUTRAL} data-testid={`admin-booking-cancel-${b.id}`}>
            Cancelar reserva
          </button>
        )}
        {b.status === "cancelled" && (
          <button onClick={() => setReactivateOpen((v) => !v)} disabled={busy} className={BTN_ACCENT} data-testid={`admin-booking-reactivate-${b.id}`}>
            <RotateCcw className="w-3.5 h-3.5" /> Reactivar reserva
          </button>
        )}
        {b.status !== "archived" && (
          <button onClick={() => setConfirming("archive")} disabled={busy} className={BTN_NEUTRAL} data-testid={`admin-booking-archive-${b.id}`}>
            <Archive className="w-3.5 h-3.5" /> Archivar
          </button>
        )}
        <button onClick={() => setConfirming("delete")} disabled={busy} className={BTN_NEUTRAL} data-testid={`admin-booking-delete-${b.id}`}>
          <Trash2 className="w-3.5 h-3.5" /> Eliminar
        </button>
      </div>

      {/* Reactivar: elegir Confirmada o Pendiente */}
      {reactivateOpen && b.status === "cancelled" && (
        <div className="mt-3 border-l-2 border-ink bg-paper-raised px-4 py-3" data-testid={`admin-reactivate-choices-${b.id}`}>
          <p className="font-ui text-[0.82rem] text-ink-soft">¿Cómo deseas reactivar esta reserva?</p>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <button
              onClick={() => patch({ status: "confirmed" }, "Reserva reactivada")}
              disabled={busy}
              className={BTN_SOLID}
              data-testid={`admin-reactivate-confirmed-${b.id}`}
            >
              Confirmada
            </button>
            <button
              onClick={() => patch({ status: "pending" }, "Reserva reactivada")}
              disabled={busy}
              className={BTN_NEUTRAL}
              data-testid={`admin-reactivate-pending-${b.id}`}
            >
              Pendiente
            </button>
            <button onClick={() => setReactivateOpen(false)} disabled={busy} className={BTN_NEUTRAL}>
              Volver
            </button>
          </div>
        </div>
      )}

      {confirming === "cancel" && (
        <ConfirmBlock
          text="¿Cancelar esta reserva? Esta reserva dejará de contar como participante confirmado."
          confirmLabel="Cancelar reserva"
          busy={busy}
          testid={`admin-cancel-confirm-${b.id}`}
          onCancel={() => setConfirming(null)}
          onConfirm={() => post(`/api/visits/admin/bookings/${b.id}/cancel`, "Reserva cancelada.")}
        />
      )}
      {confirming === "archive" && (
        <ConfirmBlock
          text="¿Archivar esta reserva? No contará en los totales ni aparecerá en la lista normal, pero permanecerá guardada en el historial."
          confirmLabel="Archivar reserva"
          busy={busy}
          testid={`admin-archive-confirm-${b.id}`}
          onCancel={() => setConfirming(null)}
          onConfirm={() => patch({ status: "archived" }, "Reserva archivada")}
        />
      )}
      {confirming === "delete" && (
        <ConfirmBlock
          text="¿Eliminar esta reserva permanentemente? Se borrará del historial y sus espacios quedarán disponibles de inmediato. Esta acción no se puede deshacer — para conservar el registro, usa Cancelar o Archivar."
          confirmLabel="Eliminar permanentemente"
          cancelLabel="Volver"
          busy={busy}
          danger
          testid={`admin-delete-confirm-${b.id}`}
          onCancel={() => setConfirming(null)}
          onConfirm={del}
        />
      )}

      {editing && (
        <EditBookingForm
          b={b}
          slot={slot}
          slots={slots}
          busy={busy}
          onSave={patch}
          onCancel={() => setEditing(false)}
        />
      )}
    </li>
  );
}

/* ——————————————————————————— Editar reserva (admin) ——— */
function EditBookingForm({ b, slot, slots, busy, onSave, onCancel }) {
  const [form, setForm] = useState({
    name: b.name,
    email: b.email,
    phone: b.phone,
    party_size: b.party_size,
    slot_id: b.slot_id,
    notes: b.notes || "",
    status: b.status === "archived" ? "cancelled" : b.status,
  });
  const activeSlots = slots.filter((s) => s.status === "active" || s.id === b.slot_id);

  const save = () => {
    onSave({
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      party_size: form.party_size,
      slot_id: form.slot_id,
      notes: form.notes.trim(),
      status: form.status,
    }, "Reserva actualizada");
  };

  return (
    <div className="mt-3 border border-line bg-paper-raised px-4 py-4" data-testid={`admin-booking-edit-form-${b.id}`}>
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className={LABEL}>Nombre</span>
          <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={FIELD} data-testid={`edit-name-${b.id}`} />
        </label>
        <label className="block">
          <span className={LABEL}>Email</span>
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={FIELD} data-testid={`edit-email-${b.id}`} />
        </label>
        <label className="block">
          <span className={LABEL}>Teléfono</span>
          <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={FIELD} data-testid={`edit-phone-${b.id}`} />
        </label>
        <div>
          <span className={LABEL}>Número de personas</span>
          <PartyStepper value={form.party_size} onChange={(v) => setForm({ ...form, party_size: v })} testid={`edit-party-${b.id}`} />
        </div>
        <label className="block sm:col-span-2">
          <span className={LABEL}>Visita (fecha y horario)</span>
          <select value={form.slot_id} onChange={(e) => setForm({ ...form, slot_id: e.target.value })} className={FIELD} data-testid={`edit-slot-${b.id}`}>
            {activeSlots.map((s) => (
              <option key={s.id} value={s.id}>
                {`${fmtDate(s.date)} · ${fmtTime(s.time)} — ${s.confirmed_count}/${s.capacity}${s.id === b.slot_id ? " (actual)" : ""}`}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className={LABEL}>Estado</span>
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={FIELD} data-testid={`edit-status-${b.id}`}>
            <option value="confirmed">Confirmada</option>
            <option value="pending">Pendiente</option>
            <option value="cancelled">Cancelada</option>
          </select>
        </label>
        <label className="block">
          <span className={LABEL}>Notas</span>
          <input type="text" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={FIELD} data-testid={`edit-notes-${b.id}`} />
        </label>
      </div>
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <button onClick={save} disabled={busy} className={BTN_SOLID} data-testid={`admin-booking-save-${b.id}`}>
          {busy ? "Guardando…" : "Guardar cambios"}
        </button>
        <button onClick={onCancel} disabled={busy} className={BTN_NEUTRAL} data-testid={`admin-booking-edit-cancel-${b.id}`}>
          Cancelar
        </button>
      </div>
    </div>
  );
}

/* ——————————————————————————— Solicitudes pendientes (global) ——— */
function PendingRow({ b, slots, token, onChange }) {
  const [busy, setBusy] = useState(false);

  const patch = async (payload, okMsg) => {
    setBusy(true);
    try {
      await apiFetch(`/api/visits/admin/bookings/${b.id}`, token, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      toast.success(okMsg);
      onChange();
    } catch (e) {
      toast.error(e.message || "No se pudo guardar el cambio. Intenta nuevamente.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="py-4" data-testid={`admin-pending-${b.id}`}>
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <p className="font-editorial text-[1.02rem] text-ink">{b.name} — {b.party_size} {b.party_size === 1 ? "persona" : "personas"}</p>
          <p className="mt-1 font-ui text-[0.75rem] text-ink-soft capitalize">{fmtDate(b.date)} · {fmtTime(b.time)} · {b.email || "sin email"} · {b.phone || "sin teléfono"}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => patch({ status: "confirmed" }, "Reserva confirmada")}
            disabled={busy}
            className={BTN_ACCENT}
            data-testid={`admin-pending-confirm-${b.id}`}
          >
            {busy ? "Procesando…" : "Confirmar reserva"}
          </button>
          <button
            onClick={async () => {
              setBusy(true);
              try {
                await apiFetch(`/api/visits/admin/bookings/${b.id}/cancel`, token, { method: "POST" });
                toast.success("Reserva cancelada.");
                onChange();
              } catch (e) {
                toast.error(e.message);
              } finally {
                setBusy(false);
              }
            }}
            disabled={busy}
            className={BTN_NEUTRAL}
            data-testid={`admin-pending-cancel-${b.id}`}
          >
            Cancelar reserva
          </button>
        </div>
      </div>
    </li>
  );
}
